import React, { useEffect, useState } from "react";
import Toggle from "../../components/common/toggle";
import toast from "react-hot-toast";

const parseBoolean = (value) => value === true || value === "true";

const Configuration = ({ settings, onUpdate, isUpdating }) => {
  const [allowExternalSharing, setAllowExternalSharing] = useState(false);
  const [autoApproveImages, setAutoApproveImages] = useState(false);
  const [retentionDays, setRetentionDays] = useState(90);
  const [localRetentionDays, setLocalRetentionDays] = useState(90);

  useEffect(() => {
    if (settings?.configurations) {
      setAllowExternalSharing(
        parseBoolean(settings.configurations.allowExternalSharing)
      );
      setAutoApproveImages(
        parseBoolean(settings.configurations.autoApproveApplicationImages)
      );

      const days = Number(settings.configurations.dataRetentionDays) || 90;
      setRetentionDays(days);
      setLocalRetentionDays(days);
    }
  }, [settings]);

  const saveConfiguration = (updates) => {
    if (!settings?._id) {
      toast.error("No settings ID found to update");
      return;
    }

    const nextAllowExternalSharing =
      updates.allowExternalSharing ?? allowExternalSharing;
    const nextAutoApproveImages =
      updates.autoApproveImages ?? autoApproveImages;
    const nextRetentionDays =
      updates.retentionDays ?? retentionDays;

    const body = {
      configurations: {
        ...(settings.configurations || {}),
        allowExternalSharing: nextAllowExternalSharing,
        autoApproveApplicationImages: nextAutoApproveImages,
        dataRetentionDays: parseInt(nextRetentionDays, 10) || 0,
      },
    };

    onUpdate(
      { id: settings._id, body },
      {
        onSuccess: () => {
          toast.success("Configuration updated!");
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to update");
        },
      }
    );
  };

  const handleAllowExternalSharingChange = (checked) => {
    setAllowExternalSharing(checked);
    saveConfiguration({ allowExternalSharing: checked });
  };

  const handleAutoApproveImagesChange = (checked) => {
    setAutoApproveImages(checked);
    saveConfiguration({ autoApproveImages: checked });
  };

  const handleRetentionBlur = () => {
    const days = parseInt(localRetentionDays, 10) || 0;
    setRetentionDays(days);
    setLocalRetentionDays(String(days));
    saveConfiguration({ retentionDays: days });
  };

  if (!settings) return null;

  return (
    <div className="settingsForm">
      <div className="card-header">
        <div className="header-text">
          <h2>Configuration</h2>
          <p>Manage system-wide configurations and data retention</p>
        </div>
      </div>
      <hr className="divider" />
      <div className="config-list">
        <div className="config-item">
          <div className="sharing-info">
            <h3>Allow External Sharing</h3>
            <p>
              Permit sharing of documents and catalogs with external parties
            </p>
          </div>
          <div className="sharing-toggle">
            <Toggle
              checked={allowExternalSharing}
              onChange={handleAllowExternalSharingChange}
              disabled={isUpdating}
            />
          </div>
        </div>

        <div className="config-item">
          <div className="sharing-info">
            <h3>Approve Application Images</h3>
            <p>Automatically approve images uploaded via the application</p>
          </div>
          <div className="sharing-toggle">
            <Toggle
              checked={autoApproveImages}
              onChange={handleAutoApproveImagesChange}
              disabled={isUpdating}
            />
          </div>
        </div>

        <div className="config-item">
          <div className="sharing-info">
            <h3>Data Retention (Days)</h3>
            <p>Number of days to retain application data</p>
          </div>
          <div className="retention-box">
            <input
              type="number"
              value={localRetentionDays}
              onChange={(e) => setLocalRetentionDays(e.target.value)}
              onBlur={handleRetentionBlur}
              disabled={isUpdating}
              style={{
                width: "80px",
                padding: "8px",
                borderRadius: "8px",
                border: "1px solid #ddd",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
